import { PublicClientApplication, Configuration, AccountInfo } from "@azure/msal-browser";
import { fetchIdentityApiData } from "@/lib/identityApiHelpers";
import { AuthStorage } from "@/hooks/useAuth";

interface EntraConfigResponse {
  organizationId: string;
  subdomain?: string;
  organizationName?: string;
  isActive: boolean;
  clientId?: string;
  tenantId?: string;
  authority?: string;
  scopes?: string[];
}

interface TokenExchangeResponse {
  accessToken: string;
  expiresAt: string;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    roles: string[];
    accessTypes: string[];
    organizationId?: string;
    employeeId?: string;
  };
}

class TokenRefreshService {
  private msalInstance: PublicClientApplication | null = null;
  private refreshInterval: NodeJS.Timeout | null = null;
  private isRefreshing = false;

  /**
   * Initialize MSAL instance for token refresh
   * Call this when app loads if user has active SSO session
   */
  async initializeMsal(): Promise<boolean> {
    try {
      // Check if user is authenticated via SSO
      const authMethod = localStorage.getItem('auth_method');
      if (authMethod !== 'Entra') {
        return false;
      }

      // Get stored Entra config
      const configStr = localStorage.getItem('entra_config');
      if (!configStr) {
        console.warn('No Entra config found for token refresh');
        return false;
      }

      const entraConfig: EntraConfigResponse = JSON.parse(configStr);

      // Initialize MSAL
      const msalConfig: Configuration = {
        auth: {
          clientId: entraConfig.clientId || '',
          authority: entraConfig.authority || `https://login.microsoftonline.com/${entraConfig.tenantId}`,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: false,
        }
      };

      this.msalInstance = new PublicClientApplication(msalConfig);
      await this.msalInstance.initialize();

      return true;
    } catch (error) {
      console.error('Failed to initialize MSAL for token refresh:', error);
      return false;
    }
  }

  /**
   * Check if access token is expiring soon
   */
  isTokenExpiringSoon(bufferMinutes: number = 10): boolean {
    const stored = AuthStorage.getStoredUser();
    if (!stored) return false;

    const expirationTime = new Date(stored.expiresAt);
    const now = new Date();
    const timeDifference = expirationTime.getTime() - now.getTime();
    const minutesUntilExpiration = timeDifference / (1000 * 60);

    return minutesUntilExpiration <= bufferMinutes;
  }

  /**
   * Silently refresh the token using MSAL
   * This is the preferred method - uses MSAL's built-in silent token refresh
   */
  async refreshToken(): Promise<string | null> {
    if (this.isRefreshing) {
      console.log('Token refresh already in progress');
      return null;
    }

    this.isRefreshing = true;

    try {
      // Check authentication method
      const authMethod = localStorage.getItem('auth_method');
      if (authMethod !== 'Entra') {
        console.log('Not using Entra auth, skipping token refresh');
        return null;
      }

      // Initialize MSAL if not already done
      if (!this.msalInstance) {
        const initialized = await this.initializeMsal();
        if (!initialized) {
          throw new Error('Failed to initialize MSAL');
        }
      }

      // Get stored MSAL account
      const accountStr = localStorage.getItem('msal_account');
      if (!accountStr) {
        throw new Error('No MSAL account found');
      }

      const account: AccountInfo = JSON.parse(accountStr);

      // Get stored Entra config
      const configStr = localStorage.getItem('entra_config');
      if (!configStr) {
        throw new Error('No Entra config found');
      }

      const entraConfig: EntraConfigResponse = JSON.parse(configStr);

      // Request new token silently (no popup)
      console.log('Refreshing Microsoft token silently...');
      const response = await this.msalInstance!.acquireTokenSilent({
        scopes: entraConfig.scopes || ["User.Read", "email", "profile", "offline_access"],
        account: account,
        forceRefresh: true, // Always get fresh token
      });

      console.log('Microsoft token refreshed successfully');

      // Exchange new Microsoft token for new Athlyn token
      const tokenExchangeResponse = await fetchIdentityApiData<TokenExchangeResponse>(
        "POST",
        "/auth/token/exchange",
        { entraAccessToken: response.accessToken }
      );

      // Update stored tokens
      const stored = AuthStorage.getStoredUser();
      if (stored && tokenExchangeResponse.user && tokenExchangeResponse.accessToken) {
        const microsoftTokens = {
          accessToken: response.accessToken,
          refreshToken: '', // Not available in token response
          expiresAt: response.expiresOn?.toISOString() || '',
          scope: entraConfig.scopes?.join(' ') || ''
        };

        // Use the AuthStorage class method directly
        sessionStorage.setItem('athlyn_cms_token', tokenExchangeResponse.accessToken);
        sessionStorage.setItem('athlyn_cms_user', JSON.stringify({
          user: tokenExchangeResponse.user,
          expiresAt: tokenExchangeResponse.expiresAt
        }));
        sessionStorage.setItem('athlyn_cms_microsoft_tokens', JSON.stringify(microsoftTokens));

        console.log('Athlyn token updated successfully');
      }

      return tokenExchangeResponse.accessToken;

    } catch (error: any) {
      console.error('Token refresh failed:', error);

      // If silent refresh fails, user needs to re-authenticate
      if (error.errorCode === 'consent_required' ||
          error.errorCode === 'interaction_required' ||
          error.errorCode === 'login_required') {
        console.warn('User interaction required - logging out');
        AuthStorage.clearAll();
        window.location.href = '/login';
      }

      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Start proactive token refresh
   * Checks every 5 minutes if token is expiring soon and refreshes it
   */
  startProactiveRefresh(): void {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Check token expiration every 5 minutes
    this.refreshInterval = setInterval(async () => {
      const authMethod = localStorage.getItem('auth_method');

      // Only refresh for SSO users
      if (authMethod === 'Entra' && this.isTokenExpiringSoon(10)) {
        console.log('Token expiring soon, refreshing...');
        await this.refreshToken();
      }
    }, 5 * 60 * 1000); // 5 minutes

    console.log('Proactive token refresh started');

    // Do an immediate check
    setTimeout(async () => {
      const authMethod = localStorage.getItem('auth_method');
      if (authMethod === 'Entra' && this.isTokenExpiringSoon(10)) {
        console.log('Initial token check - refreshing...');
        await this.refreshToken();
      }
    }, 1000);
  }

  /**
   * Stop proactive token refresh
   */
  stopProactiveRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('Proactive token refresh stopped');
    }
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
